import { inputClass, mfgPrimaryBtnClass, ghostBtnClass } from "@/components/manufacturing/manufacturingSurfaceClasses";
import { OVERHEAD_BUCKET_SENSITIVE_NOTE } from "@/lib/manufacturing/overheadObligation";
import type { MfgBucketType, MfgOverheadBucket } from "@/lib/manufacturing/overheadTypes";
import type { OverheadBucketDraft } from "./useManufacturingOverheadBuckets";

type Props = {
  title: string;
  draft: OverheadBucketDraft;
  setDraft: (next: OverheadBucketDraft | ((prev: OverheadBucketDraft) => OverheadBucketDraft)) => void;
  bucketTypes: MfgBucketType[];
  parentOptions: MfgOverheadBucket[];
  busy: boolean;
  onCancel: () => void;
  onSubmit: () => void;
};

export function OverheadBucketEditForm({
  title,
  draft,
  setDraft,
  bucketTypes,
  parentOptions,
  busy,
  onCancel,
  onSubmit,
}: Props) {
  return (
    <div className="rounded-xl border border-indigo-200 bg-indigo-50/40 p-3 dark:border-indigo-900 dark:bg-indigo-950/20">
      <div className="text-sm font-semibold text-gray-900 dark:text-zinc-100">{title}</div>
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <label className="block text-xs font-medium text-gray-700 dark:text-zinc-300">
          Name
          <input
            className={`${inputClass} mt-1`}
            value={draft.name}
            onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
          />
        </label>
        <label className="block text-xs font-medium text-gray-700 dark:text-zinc-300">
          Bucket type
          <select
            className={`${inputClass} mt-1`}
            value={draft.type_key}
            onChange={(e) => setDraft((d) => ({ ...d, type_key: e.target.value }))}
          >
            {bucketTypes.map((t) => (
              <option key={t.id} value={t.type_key}>
                {t.name}
              </option>
            ))}
          </select>
        </label>
        <label className="block text-xs font-medium text-gray-700 dark:text-zinc-300">
          Parent bucket (optional)
          <select
            className={`${inputClass} mt-1`}
            value={draft.parent_bucket_id}
            onChange={(e) => setDraft((d) => ({ ...d, parent_bucket_id: e.target.value }))}
            disabled={draft.is_aggregate}
          >
            <option value="">(top level)</option>
            {parentOptions.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name} ({b.bucket_key})
              </option>
            ))}
          </select>
        </label>
        <label className="block text-xs font-medium text-gray-700 dark:text-zinc-300">
          Category
          <input
            className={`${inputClass} mt-1`}
            value={draft.category}
            onChange={(e) => setDraft((d) => ({ ...d, category: e.target.value }))}
          />
        </label>
        <label className="block text-xs font-medium text-gray-700 dark:text-zinc-300 sm:col-span-2">
          Notes
          <input
            className={`${inputClass} mt-1`}
            value={draft.notes}
            onChange={(e) => setDraft((d) => ({ ...d, notes: e.target.value }))}
          />
        </label>
        <label className="flex items-center gap-2 text-xs font-medium text-gray-700 dark:text-zinc-300 sm:col-span-2">
          <input
            type="checkbox"
            checked={draft.is_aggregate}
            onChange={(e) => setDraft((d) => ({
              ...d,
              is_aggregate: e.target.checked,
              parent_bucket_id: e.target.checked ? "" : d.parent_bucket_id,
              tracks_balance: e.target.checked ? false : d.tracks_balance,
            }))}
          />
          Aggregate bucket. It has no payment of its own and totals the buckets under it.
        </label>
        {draft.is_aggregate ? null : (
          <label className="flex items-center gap-2 text-xs font-medium text-gray-700 dark:text-zinc-300 sm:col-span-2">
            <input
              type="checkbox"
              checked={draft.tracks_balance}
              onChange={(e) => setDraft((d) => ({ ...d, tracks_balance: e.target.checked }))}
            />
            This item has a balance, such as a loan. A lease leaves this off.
          </label>
        )}
        {draft.tracks_balance && !draft.is_aggregate ? (
          <>
            <label className="block text-xs font-medium text-gray-700 dark:text-zinc-300">
              Balance
              <input
                type="number"
                min={0}
                step="0.01"
                className={`${inputClass} mt-1`}
                value={draft.balance_start_usd}
                onChange={(e) => setDraft((d) => ({ ...d, balance_start_usd: e.target.value }))}
              />
            </label>
            <label className="block text-xs font-medium text-gray-700 dark:text-zinc-300">
              Balance as of
              <input
                type="month"
                className={`${inputClass} mt-1`}
                value={draft.balance_as_of}
                onChange={(e) => setDraft((d) => ({ ...d, balance_as_of: e.target.value }))}
              />
            </label>
            <label className="block text-xs font-medium text-gray-700 dark:text-zinc-300 sm:col-span-2">
              Principal paid down each month
              <input
                type="number"
                min={0}
                step="0.01"
                className={`${inputClass} mt-1`}
                value={draft.principal_monthly_usd}
                onChange={(e) => setDraft((d) => ({ ...d, principal_monthly_usd: e.target.value }))}
              />
            </label>
          </>
        ) : null}
      </div>
      <p className="mt-3 text-xs text-gray-600 dark:text-zinc-400">{OVERHEAD_BUCKET_SENSITIVE_NOTE}</p>
      <div className="mt-3 flex flex-wrap gap-2">
        <button type="button" className={mfgPrimaryBtnClass} disabled={busy} onClick={onSubmit}>
          {busy ? "Saving…" : "Save"}
        </button>
        <button type="button" className={ghostBtnClass} disabled={busy} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}
